import { Modal } from '@ag1/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { subscriptionShipmentFrequencyEvent } from 'analytics/events';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ShortLocaleEnum } from 'schema/locale';
import usePageId from 'shared/hooks/usePageId';
import { showNotification } from 'shared/ui/Notification';
import { getFrequencyEditData } from 'shared/utils/getFrequencyEditData';
import { pluralize } from 'shared/utils/pluralize';
import { useLanguageValue } from 'state/hooks/useLanguage';
import { useQueryGetSubscriptionDetailsV2 } from 'state/internal/getSubscriptionDetails';
import { useMutationUpdateSubscriptionDetailsV2 } from 'state/internal/updateSubscriptionDetails';
import { z } from 'zod';
import { useCreatePerformanceTimer } from '../../hooks/useCreatePerformanceTimer';
import SelectDateChangeV2 from '../SelectOrderDateV2/components/SelectDateChangeV2';
import SelectDateSuccessV2 from '../SelectOrderDateV2/components/SelectDateSuccessV2';
import { CHANGE_ORDER_DATE_STEP } from '../SelectOrderDateV2/constants';
import useSelectOrderDateModalV2 from '../SelectOrderDateV2/hooks/useSelectOrderDateModalV2';
import { type DeliveryFrequencyV2Props } from './DeliveryFrequencyV2.types';
import { DeliveryFrequencyView } from './components/DeliveryFrequencyView';
import { CHANGE_FREQUENCY_STEP } from './constants';
import useDeliveryFrequencyModalV2 from './hooks/useDeliveryFrequencyModalV2';

const validationSchema = z.object({
  deliveryOptions: z.string()
});

type FieldValues = z.infer<typeof validationSchema>;

const DeliveryFrequencyV2 = ({
  isOpen,
  onClose,
  referenceId,
  intervals,
  unit,
  // isUnifiedCheckout = false,
  // setNewDateValue,
  currentInterval
}: DeliveryFrequencyV2Props) => {
  const { t } = useTranslation('modalWindows');
  const router = useRouter();
  const language = useLanguageValue();

  const pageId = usePageId();
  const contractId = referenceId || pageId;

  const { data: subscriptionDetails } = useQueryGetSubscriptionDetailsV2(contractId);
  const { next_order_date: newDateValue = 0 } = subscriptionDetails || {};

  const { mutateAsync: updateSubscription, isPending: isLoadingStatus } =
    useMutationUpdateSubscriptionDetailsV2();

  const { step: changeFrequencyStep } = useDeliveryFrequencyModalV2();
  const { step: orderDateStep } = useSelectOrderDateModalV2();

  // const { data: subscription } = useQueryGetSubscriptionDetailsV2(contractId);
  // const productsList = useMemo(() => subscription?.line_items || [], [subscription]);

  // const threeMonthPouchProduct = useMemo(
  //   () =>
  //     checkVariantInProductList(
  //       STORE_SPECIFIC_UNIQUE_VARIANTS_MAP[store].threeMonthPouch || 0,
  //       productsList
  //     ),
  //   [productsList, store]
  // );

  // const pouchProduct = useMemo(
  //   () =>
  //     checkVariantInProductList(STORE_SPECIFIC_UNIQUE_VARIANTS_MAP[store].pouch || 0, productsList),
  //   [productsList, store]
  // );

  const { intervals: intervalFrequencyList, unit: frequencyUnit } = useMemo(() => {
    const langUnit = language === ShortLocaleEnum.de ? 'Tage' : unit;

    return getFrequencyEditData(intervals, langUnit);
  }, [intervals, language, unit]);

  const createPerformanceTimer = useCreatePerformanceTimer();

  const {
    handleSubmit,
    reset,
    formState: { isValid, isSubmitting },
    watch,
    control
  } = useForm<FieldValues>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      deliveryOptions: currentInterval
    }
  });

  useEffect(() => {
    if (!isOpen) {
      reset({
        deliveryOptions: currentInterval
      });
    }
  }, [isOpen, reset, currentInterval]);

  const onSubmit = useCallback(
    async (data: FieldValues, e?: React.BaseSyntheticEvent) => {
      e?.preventDefault();

      const newInterval = Number(data.deliveryOptions);
      const payload = {
        frequency_count: newInterval
      };

      const timer = createPerformanceTimer('membersSubscriptionChangeFrequency', {
        membersSubscriptionOldFrequency: `${currentInterval} ${unit}`,
        membersSubscriptionNewFrequency: `${newInterval} ${unit}`,
        membersSubscriptionFrequencyDiff: newInterval - Number(currentInterval)
      });

      if (contractId) {
        await updateSubscription(
          { contractId, data: payload },
          {
            onSuccess: () => {
              showNotification('mutationSuccess', {
                title: t('editFrequency.infoMessage', {
                  date:
                    language === ShortLocaleEnum.de
                      ? `${newInterval} ${frequencyUnit}`
                      : pluralize(Number(newInterval), frequencyUnit)
                })
              });
              onClose();
              timer.success();
              subscriptionShipmentFrequencyEvent({
                checkoutType: 'unified',
                location: router.asPath,
                shipFrequency: String(newInterval),
                subscriptionId: contractId
              });
            },
            onError: (error: Error) => {
              timer.failure(error);
              showNotification('mutationError');
            }
          }
        );
      }
    },
    [
      unit,
      currentInterval,
      contractId,
      t,
      language,
      frequencyUnit,
      onClose,
      createPerformanceTimer,
      updateSubscription,
      router
    ]
  );

  const isDisabled =
    (watch('deliveryOptions') === String(currentInterval) && isValid) ||
    !isValid ||
    isSubmitting ||
    isLoadingStatus;

  const handleSubmitInternal = handleSubmit((data, e) => {
    onSubmit(data, e);
    // TODO: need clarification on downgrade From Free Shipping
    // if (
    //   downgradeFromFreeShipping({
    //     variantId: threeMonthPouchProduct?.variant_id,
    //     interval: data.deliveryOptions,
    //     store
    //   })
    // ) {
    //   if (locale === 'en-qa') toggleChangeFreeShippingModal();
    //   else {
    //     handleSubmit(onSubmit)();
    //   }
    // } else {
    // handleSubmit(onSubmit)();
  });

  return (
    <Modal onClose={onClose} isOpen={isOpen}>
      {changeFrequencyStep === CHANGE_FREQUENCY_STEP.frequency && (
        <DeliveryFrequencyView
          isLoadingStatus={isLoadingStatus}
          handleSubmitInternal={handleSubmitInternal}
          intervalFrequencyList={intervalFrequencyList}
          control={control}
          isSubmitting={isSubmitting}
          isDisabled={isDisabled}
          frequencyUnit={frequencyUnit}
          currentInterval={Number(currentInterval)}
          newDateValue={newDateValue}
        />
      )}
      {changeFrequencyStep === CHANGE_FREQUENCY_STEP.calendar && (
        <>
          {orderDateStep === CHANGE_ORDER_DATE_STEP.select && (
            <SelectDateChangeV2
              referenceId={referenceId}
              intervalFrequency={Number(currentInterval)}
              onClose={onClose}
            />
          )}
          {orderDateStep === CHANGE_ORDER_DATE_STEP.success && (
            <SelectDateSuccessV2 ctaOnClick={onClose} />
          )}
        </>
      )}
    </Modal>
  );
};

export default DeliveryFrequencyV2;
